import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, Output, EventEmitter, ContentChild, TemplateRef, OnDestroy, Inject, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { curveMonotoneX } from 'd3-shape';
import "core-js";
import { APP_BASE_HREF } from '@angular/common';
import { BehaviorSubject, combineLatest, empty, EMPTY, fromEvent, interval, merge, NEVER, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, debounce, debounceTime, distinctUntilChanged, map, startWith, switchMap, tap, throttleTime } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { v4 as uuidv4 } from 'uuid';
import { Color } from '@swimlane/ngx-charts';
import { GraphQlQuery, GraphQlResultsSeriesOptions, IGraphQlService } from '../../../services/GraphQl/IGraphQL.service';
import { NgxMultiExcludes } from '../../../operators/ngx/multi-excludes/ngx-multi-excludes.operator';
import { GraphQlQueryExecutor } from '../../../operators/graphql/query-executor/graphql-query-executor.operator';
import { GraphQlQueryResultToNgxMulti } from '../../../operators/graphql/query-result-to-ngx-multi/graphql-query-result-to-ngx-multi.operator';
import { NgxMultiTimeline } from '../../../operators/ngx/multi-timeline/multi-timeline.operator';
import { EveInternal } from '../../../operators/eve/interval/eve-interval.operator';
import { IVisibilityStateService } from '../../../services/VisibilityState/IVisibilityState.service';

@Component({
  selector: 'graphql-line-chart',
  templateUrl: './linechart.component.html',
  styleUrls: ['./linechart.component.css']
})
export class GraphQlLineChartComponent implements OnInit, OnChanges, OnChanges {


  

  // options
  legend: boolean = true;
  legendTitle: string = "";
  legendPosition: string = 'bottom';
  showLabels: boolean = true;
  animations: boolean = true;
  xAxis: boolean = true;
  yAxis: boolean = true;
  showYAxisLabel: boolean = true;
  showXAxisLabel: boolean = true;
  //xAxisLabel: string = 'Year';
  //yAxisLabel: string = 'Population';
  timeline: boolean = true;
  curve: any = curveMonotoneX;

  scheme = {
    domain: ['#00abca', '#be0078', '#85bf75', '#c23133']
  };



  @Input()
  query: GraphQlQuery = null;
  queryObserable: ReplaySubject<GraphQlQuery> = new ReplaySubject<GraphQlQuery>(1);

  @Input()
  graphQlResultsSeriesOptions: GraphQlResultsSeriesOptions = null;
  

  @Input()
  options: LineChartOptions = null;

  @Output()
  select: EventEmitter<any> = new EventEmitter();

  @ContentChild('eveTooltipTemplate') eveTooltipTemplate: TemplateRef<any>;
  @ContentChild('eveSeriesTooltipTemplate') eveSeriesTooltipTemplate: TemplateRef<any>;
  

  excludeResults: BehaviorSubject<String[]> = new BehaviorSubject([]);
  resultsObserable = this.queryObserable.pipe(
    switchMap(query => of(query)
      .pipe(
        GraphQlQueryExecutor(of(this.graphQlService)),
        EveInternal(5000, this.visibilityStateService.VisibilityState()),
        GraphQlQueryResultToNgxMulti(),
        NgxMultiExcludes(this.excludeResults),
        NgxMultiTimeline(of(null), of(null), of(query.groupBys[0].timeUnit)),
        catchError(val => {
          return NEVER;
        }),
        map(multi => {
          if (this.graphQlResultsSeriesOptions != null) multi.forEach(m => m.series.forEach(s => s.value = this.graphQlResultsSeriesOptions.valueConverter(s.value)))
          return multi;
        })
      )
    )
    
  );


    
  parentWidth: Observable<number> = interval(10).pipe(
    map(() => this.parentElement.offsetWidth),
    distinctUntilChanged()
  );

  parentHeight: Observable<number> = interval(10).pipe(
    map(() => this.parentElement.offsetHeight),
    distinctUntilChanged()
  );

  view: Observable<number[]> = combineLatest(this.parentWidth, this.parentHeight).pipe(
    map(a => [a[0], a[1] - 100]),
    debounceTime(15)
  );

  parentElement: any = null;

  legendStyles: Observable<SafeHtml> = this.excludeResults.pipe(
    map(excludeResult => {
      return this.sanitizer.bypassSecurityTrustHtml(excludeResult.map((e, p) => `<style>
      .${this.componenetId} ngx-charts-legend-entry [title="${e}"] {
        opacity: 0.2;
      }
    </style>`
      ).join("\r\n"))
    })
  );

  componenetId: String = "componenetId_" + uuidv4().replaceAll("-", "");

  constructor(
     @Inject("BASE_REPORTING") public reportingUrl: string,
    public graphQlService: IGraphQlService,
    private elRef: ElementRef,
    private sanitizer: DomSanitizer,
    public visibilityStateService: IVisibilityStateService
  ) {

  }

  ngOnInit() {
    this.elRef.nativeElement.classList.add(this.componenetId);

    this.parentElement = this.elRef.nativeElement.parentElement;
    this.queryObserable.next(this.query);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["query"] != null) this.queryObserable.next(this.query);
  }


  

  

  onSelect(data): void {
    if (!this.isLegend(data)) {
      this.select.emit({ result: data, query: this.query });
    } else {
      const excludeResults = this.excludeResults.getValue()

      if (excludeResults.indexOf(data) == -1) this.excludeResults.next(excludeResults.concat([data]));
      else this.excludeResults.next(excludeResults.filter(i => i != data));
    }
  }
  isLegend = (event) => typeof event === 'string';



  onActivate(data): void {
  }

  onDeactivate(data): void {
  }

}





export interface LineChartOptions {
  width: number,
  height: number,
  chartHeader?: string,
  xAxisLabel?: string,
  yAxisLabel?: string,
  xAxisTickFormatting?: Function,
  colourScheme: string | Color
}
